import { matchPath } from 'react-router-dom';
import { _take, takeOnce } from '../../listenerMiddleware';
import * as appActions from '../app/actions';
import * as categoryActions from '../category/actions';
import * as finderActions from '../finder/actions';
import * as projectActions from '../project/actions';
import * as uploaderActions from '../uploader/actions';
import * as viewModeActions from '../viewMode/actions';
import { getPermissions } from '../app/selectors';
import { getCategories } from '../category/selectors';
import { getProject, getSelectedIds } from '../project/selectors';
import { Permission } from '../../../general/model';
import { ViewMode } from '../viewMode/model';
import { ALL_CATEGORIES_ID } from '../category/model';
import { navigateToRoute, routes } from '../router/model';
import { parseLocationHighlightProjectId } from '../lastState/model';
import {
  DEMO_FILES_CATEGORY_NAME,
  clearDemoUploadIntent,
  normalizeName,
  parseLocationDemoUpload,
  readDemoUploadIntent,
  removeDemoUploadParamFromLocation,
} from './model';
import { getDemoWelcomeModeEnabled, getOwnSpaceId, getSettingsLoaded } from './selectors';

// Demo welcome flow (MYUA-17010), active only when the instance setting
// `demo_welcome_mode_enabled` is true:
// - regular entry: the user's own "Your space" is selected by default;
// - special link (?demo_upload=1 or a saved intent): Your space -> Files -> upload modal.
//
// Race rule: projectIdsSelected -> loadCategoriesRequested -> loadDictionary runs
// synchronously inside dispatch, and on a cache hit `categoriesChanged` is dispatched
// synchronously too. So:
// 1. a `_take` is created BEFORE the dispatch that causes the awaited event;
// 2. a state check and its `_take` sit in one synchronous block (no await between);
// 3. every created `_take` is awaited (a dangling one is aborted when the effect
//    ends and surfaces as an unhandled TaskAbortError).
export const demoWelcomeListeners = () => {
  watchInitialized();
};

function watchInitialized() {
  takeOnce(appActions.initialized, async (action, listenerApi) => {
    const { getState, dispatch, signal } = listenerApi;

    const permissions = getPermissions(getState());
    if (!permissions[Permission['fs.assetlab_access']]) return;

    // 1) Settings are loaded in parallel with the last state and may still be pending.
    //    If /settings failed, `settingsLoaded` never comes: we keep waiting and the demo
    //    flow simply doesn't run — no flag, no feature. No timeout on purpose.
    if (!getSettingsLoaded(getState())) {
      await _take(listenerApi, appActions.settingsLoaded);
      if (signal.aborted) return;
    }

    const uploadIntent = parseLocationDemoUpload() || readDemoUploadIntent();
    if (!getDemoWelcomeModeEnabled(getState())) {
      // Don't keep an intent collected on a non-demo instance.
      clearDemoUploadIntent();
      removeDemoUploadParamFromLocation();
      return;
    }

    // 2) An explicit deep link from Creative Hub wins over the demo flow.
    if (parseLocationHighlightProjectId() !== null) return;

    // 3) Projects are requested by app/listeners.watchPagePathChanged.
    const projectState = getProject(getState());
    if (projectState.projects.length === 0 || projectState.loading) {
      await _take(listenerApi, projectActions.projectsChanged);
      if (signal.aborted) return;
    }

    const ownSpaceId = getOwnSpaceId(getState());
    if (ownSpaceId === null) {
      clearDemoUploadIntent();
      removeDemoUploadParamFromLocation();
      return;
    }

    if (!uploadIntent) {
      // Regular entry: only "Your space is open by default", no modal. A restored
      // last state with a specific project is kept as is.
      if (getSelectedIds(getState()).length === 0) {
        dispatch(viewModeActions.changed({ viewMode: ViewMode.assets }));
        dispatch(projectActions.projectIdsSelected({ ids: [ownSpaceId], saveLastState: false }));
      }
      return;
    }

    // Special link: Your space -> Files -> upload modal. One-shot.
    clearDemoUploadIntent();
    removeDemoUploadParamFromLocation();

    // UploadModal is mounted only inside UploadButton on ViewerPage, so make sure we
    // are on the viewer. Detect the page by location (like isSharingPage in index.tsx):
    // the router state may not be updated yet.
    if (!matchPath((window.location.hash || '').substring(1), routes.viewer)) {
      navigateToRoute(routes.viewer);
    }

    dispatch(viewModeActions.changed({ viewMode: ViewMode.assets }));
    // Subscribe BEFORE dispatch and always await: watchProjectSelected (takeLatest,
    // no comparison of the selection) always leads to `categoriesChanged` — from the
    // cache synchronously, from the network asynchronously, on error via
    // clearOnError -> itemsChanged([]).
    const categoriesP = _take(listenerApi, categoryActions.categoriesChanged);
    dispatch(projectActions.projectIdsSelected({ ids: [ownSpaceId], saveLastState: false }));
    await categoriesP;
    if (signal.aborted) return;

    // If "Files" is missing (renamed/removed), the modal is opened anyway: the user
    // picks a folder there, and uploading stays disabled until one is selected.
    const files = getCategories(getState()).find(
      (c) =>
        c.path.length === 0 && normalizeName(c.name) === normalizeName(DEMO_FILES_CATEGORY_NAME),
    );
    if (files) {
      dispatch(
        categoryActions.setExpandedKeys({
          keys: [ALL_CATEGORIES_ID, files.id],
          saveLastState: false,
        }),
      );
      dispatch(
        categoryActions.categorySelected({
          id: files.id,
          saveLastState: false,
          log: { category: files },
        }),
      );
    }
    dispatch(finderActions.assetSelected({ ids: [], firstId: undefined, lastId: undefined }));
    dispatch(finderActions.setDetailsVisible({ visible: false }));
    dispatch(uploaderActions.modalOpened());
  });
}
